// People & Badges rules. Words: PEOPLE_BADGES_WORDS.md (approved 2026-10-02),
// PEOPLE_BADGES_COPY_WORDS.md (approved 2026-10-06). Mockup: MOCKUP_PEOPLE_BADGES_APPROVED.html.
// LOTO verify: QEW_DASHBOARD_WORDS.md, MOCKUP_QEW_DASHBOARD_APPROVED.html screen 6.
// Pure: no database. Shared by the page, the server steps (functions/src/people.ts)
// and the unit tests, so the page and the server refuse the same things.
//
// Storage, as built (copy words): the tag is the badge (badges/{tagId}), the human is
// the person (people/{qewId}). Authority rides on the badge. The Active switch on a
// person turns the badge on or off. A QEW is never deleted and an ID is never reused.

import { addYears, LOTO_VALID_YEARS } from "./claim";
import { lotoReportProblem } from "./loto";
import { nextFreeTag, tagNumber } from "./new-trainee";
import type { Authority, Badge } from "./badge";

// ---- What Firestore holds ----

/** companies/{company}/people/{qewId}. Fields the page reads. */
export interface Person {
  qewId: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  siteNumber: string;
  department?: string | null;
  role?: string | null;
  supervisor?: string | null;
  employeeId?: string | null;
  yearsExperience?: string | number | null;
  level?: string | null;
  /** Carried QEW status from the Sheet. Counts toward "Qualified, Active". */
  isActive?: boolean;
  assignedOn?: string | null;
  claimedOn?: string | null;
  claimedSessionId?: string | null;
  notes?: string | null;
  lotoTrainedOn?: string | null;
  lotoExpiresOn?: string | null;
  lotoReportedOn?: string | null;
  lotoNoneReported?: boolean;
  lotoVerifiedOn?: string | null;
  lotoVerifiedByName?: string | null;
  lotoSource?: string | null;
  lotoLastVerifiedOn?: string | null;
  /** Picked by the QEW on his glove card (GLOVES_REWORK_WORDS.md). */
  gloveSize?: string | null;
}

export type BadgeDoc = Badge & {
  claimedOn?: string | null;
  claimedSessionId?: string | null;
  outOfServiceOn?: string | null;
  outOfServiceReason?: string | null;
  outOfServiceByName?: string | null;
  disabledReason?: string | null;
};

// ---- Levels and authorizations ----

export const LEVELS = ["QEW1", "QEW2", "Contractor"] as const;
export type Level = (typeof LEVELS)[number];

/** The three switches on a person. Other authorities (admin, ieaWalk) are not shown here. */
export const PERSON_AUTHORITIES: { key: Authority; label: string; hint: string }[] = [
  { key: "energizedWork", label: "Energized Work", hint: "Can choose Tier 3 energized work in the JSP" },
  { key: "eswc", label: "ESWC", hint: "Can set up an electrically safe work condition" },
  { key: "troubleshooting", label: "Troubleshooting", hint: "Can troubleshoot with the panel open" },
];

/**
 * The switch on an admin badge (IEA_OFFLINE_SHELL_WORDS.md section 4, approved 2026-10-14).
 * The authority keeps its stored name ieaWalk. Screens say IEA Site Survey.
 */
export const ADMIN_AUTHORITIES: { key: Authority; label: string; hint: string }[] = [
  { key: "ieaWalk", label: "IEA Site Survey", hint: "Can run the IEA Site Survey and its field data" },
];

/** The screen name for any switch, person or admin. */
export function authorityLabel(key: Authority): string {
  return [...PERSON_AUTHORITIES, ...ADMIN_AUTHORITIES].find((a) => a.key === key)?.label ?? key;
}

/** QEW1: energized off, the other two on. QEW2: all on. Contractor: same as QEW1 until set by hand. */
export function levelDefaults(level: string): Authority[] {
  return level === "QEW2" ? ["energizedWork", "eswc", "troubleshooting"] : ["eswc", "troubleshooting"];
}

export function levelLabel(level: string | null | undefined): string {
  if (!level) return "No level";
  if (level === "TRAINEE") return "Trainee";
  return level;
}

/** What the confirm says the field can do after the change. */
export function authorityChangeLine(name: string, key: Authority, on: boolean): string {
  const first = name.split(" ")[0] || name;
  switch (key) {
    case "energizedWork":
      return on
        ? `Off to on. ${first} can choose Tier 3 energized work in the JSP flow starting with the next sign-in.`
        : `On to off. ${first} can no longer choose Tier 3 energized work. The JSP flow stops it at the next sign-in.`;
    case "eswc":
      return on ? `Off to on. ${first} can set up an ESWC starting with the next sign-in.` : `On to off. ${first} can no longer set up an ESWC after the next sign-in.`;
    case "ieaWalk":
      return on
        ? `Off to on. ${first} can run the IEA Site Survey starting with the next app open with signal.`
        : `On to off. ${first} can no longer run the IEA Site Survey. Saves still waiting on a device are refused.`;
    default:
      return on
        ? `Off to on. ${first} can troubleshoot starting with the next sign-in.`
        : `On to off. ${first} can no longer troubleshoot after the next sign-in.`;
  }
}

// ---- The people list ----

export type PersonState = "qualified" | "training" | "off";

export interface PersonRow {
  qewId: string;
  name: string;
  siteNumber: string;
  level: string;
  state: PersonState;
  /** Badge Status Disabled because the tag was taken out of service. */
  tagOut: boolean;
  lotoPending: boolean;
  isAdmin: boolean;
}

/** A reported LOTO date nobody has verified yet. */
export function lotoPending(p: Pick<Person, "lotoTrainedOn" | "lotoVerifiedOn">): boolean {
  return Boolean(p.lotoTrainedOn && !p.lotoVerifiedOn);
}

/**
 * The pill on a row. Off: the badge is switched off or the tag is out of service.
 * Qualified: carried Active from the Sheet, or Nodes 1 to 4 plus a verified LOTO date
 * (same rule as the Dashboard top block, 2026-10-09). Training: everyone else with a working tag.
 */
export function personState(p: Person, badge: BadgeDoc | undefined, trainingComplete: boolean): PersonState {
  if (!badge || badge.Status !== "Active") return "off";
  const verified = Boolean(p.lotoVerifiedOn || p.lotoLastVerifiedOn);
  return p.isActive || (trainingComplete && verified) ? "qualified" : "training";
}

export function isAdminId(id: string): boolean {
  return id.startsWith("ALL-");
}

export function rowsFor(people: Person[], badges: Map<string, BadgeDoc>, nodesDone: ReadonlySet<string>): PersonRow[] {
  return people.map((p) => {
    const b = badges.get(p.qewId);
    return {
      qewId: p.qewId,
      name: p.name || p.qewId,
      siteNumber: isAdminId(p.qewId) ? "ALL" : p.siteNumber,
      level: isAdminId(p.qewId) ? "Admin" : levelLabel(p.level),
      state: isAdminId(p.qewId) ? "qualified" : personState(p, b, nodesDone.has(p.qewId)),
      tagOut: Boolean(b?.outOfServiceOn),
      lotoPending: !isAdminId(p.qewId) && lotoPending(p),
      isAdmin: isAdminId(p.qewId),
    };
  });
}

export interface PeopleGroup {
  siteNumber: string;
  rows: PersonRow[];
}

/** Admins first, then by site number. IDs in tag order inside. Search by name or ID. */
export function groupPeople(rows: PersonRow[], search = "", onlyLotoPending = false): PeopleGroup[] {
  const q = search.trim().toLowerCase();
  const groups = new Map<string, PersonRow[]>();
  for (const r of rows) {
    if (onlyLotoPending && !r.lotoPending) continue;
    if (q && !r.name.toLowerCase().includes(q) && !r.qewId.toLowerCase().includes(q)) continue;
    const list = groups.get(r.siteNumber) ?? [];
    list.push(r);
    groups.set(r.siteNumber, list);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === "ALL" ? -1 : b === "ALL" ? 1 : a.localeCompare(b)))
    .map(([siteNumber, list]) => ({ siteNumber, rows: list.sort((x, y) => x.qewId.localeCompare(y.qewId, "en", { numeric: true })) }));
}

// ---- Tags ----

export type TagBucket = "claimed" | "unclaimed" | "out";

export function tagBucket(b: BadgeDoc): TagBucket {
  if (b.Status === "Unclaimed") return "unclaimed";
  if (b.Status === "Active") return "claimed";
  return "out";
}

export interface TagCounts {
  siteNumber: string;
  claimed: number;
  unclaimed: number;
  out: number;
}

/** QEW tags per site. Admin badges are left out. */
export function tagCounts(badges: BadgeDoc[], siteNumbers: string[]): TagCounts[] {
  return [...siteNumbers].sort().map((s) => {
    const mine = badges.filter((b) => b["Badge ID"].startsWith(`${s}-`) && !isAdminId(b["Badge ID"]));
    return {
      siteNumber: s,
      claimed: mine.filter((b) => tagBucket(b) === "claimed").length,
      unclaimed: mine.filter((b) => tagBucket(b) === "unclaimed").length,
      out: mine.filter((b) => tagBucket(b) === "out").length,
    };
  });
}

export function tagsIn(badges: BadgeDoc[], siteNumber: string, bucket: TagBucket): BadgeDoc[] {
  return badges
    .filter((b) => b["Badge ID"].startsWith(`${siteNumber}-`) && !isAdminId(b["Badge ID"]) && tagBucket(b) === bucket)
    .sort((a, b) => (tagNumber(a["Badge ID"]) ?? 0) - (tagNumber(b["Badge ID"]) ?? 0) || a["Badge ID"].localeCompare(b["Badge ID"]));
}

/** The tag Add Person will take: the lowest unclaimed QEW tag at the site with no person record. */
export function nextTagForSite(badges: BadgeDoc[], people: Person[], siteNumber: string): string | null {
  const unclaimed = badges.filter((b) => b.Status === "Unclaimed").map((b) => b["Badge ID"]);
  return nextFreeTag(unclaimed, siteNumber, new Set(people.map((p) => p.qewId)));
}

// ---- Edits: the same checks run on the page and on the server ----

export interface PersonInput {
  name: string;
  email: string;
  phone: string;
  department: string;
  role: string;
  supervisor: string;
  yearsExperience: string;
  employeeId: string;
  level: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** keepLevel: the level already on the record (TRAINEE from a claim) stays allowed until changed. */
export function personProblem(input: PersonInput, keepLevel = ""): string | null {
  if (!input.name.trim()) return "Enter the name.";
  if (input.name.trim().length > 80) return "Keep the name under 80 characters.";
  if (input.email.trim() && !EMAIL.test(input.email.trim())) return "Check the email address.";
  for (const [label, v] of [
    ["Phone", input.phone],
    ["Department", input.department],
    ["Role", input.role],
    ["Supervisor", input.supervisor],
    ["Experience", input.yearsExperience],
    ["Employee ID", input.employeeId],
  ] as const) {
    if (v.trim().length > 80) return `Keep ${label.toLowerCase()} under 80 characters.`;
  }
  if (!(LEVELS as readonly string[]).includes(input.level) && input.level !== keepLevel) return "Pick a level.";
  return null;
}

export function cleanPerson(input: PersonInput): PersonInput {
  const s = (v: string) => v.trim().replace(/\s+/g, " ");
  return {
    name: s(input.name),
    email: input.email.trim().toLowerCase(),
    phone: s(input.phone),
    department: s(input.department),
    role: s(input.role),
    supervisor: s(input.supervisor),
    yearsExperience: s(input.yearsExperience),
    employeeId: s(input.employeeId),
    level: input.level.trim(),
  };
}

export interface NewPersonInput {
  siteNumber: string;
  name: string;
  email: string;
  level: string;
}

export function newPersonProblem(input: NewPersonInput): string | null {
  if (!/^\d{2}$/.test(input.siteNumber)) return "Pick the site.";
  if (!input.name.trim()) return "Enter the name.";
  if (input.name.trim().length > 80) return "Keep the name under 80 characters.";
  if (input.email.trim() && !EMAIL.test(input.email.trim())) return "Check the email address.";
  if (!(LEVELS as readonly string[]).includes(input.level)) return "Pick a level.";
  return null;
}

export const OUT_OF_SERVICE_REASONS = ["Lost", "Damaged", "Other"] as const;

export function outOfServiceProblem(reason: string, note: string): string | null {
  if (!(OUT_OF_SERVICE_REASONS as readonly string[]).includes(reason)) return "Pick a reason.";
  if (reason === "Other" && !note.trim()) return "Say what happened.";
  if (note.trim().length > 200) return "Keep the note under 200 characters.";
  return null;
}

// ---- LOTO verify ----

export function lotoSourceLine(source: string | null | undefined): string {
  if (source === "Sheet copy") return "Carried from the Sheet";
  if (source === "Tag claim") return "Reported at the tag claim";
  if (source === "Training Portal") return "Entered in the Training Portal";
  if (source === "QEW Dashboard") return "Reported on the QEW Dashboard";
  return source || "Reported";
}

/**
 * Why a verify is refused. A blank correction confirms the date as reported.
 * Someone else verifies: never the QEW himself.
 */
export function verifyProblem(p: Pick<Person, "qewId" | "lotoTrainedOn" | "lotoVerifiedOn">, verifierBadgeId: string, correctedDate: string, today: string): string | null {
  if (!p.lotoTrainedOn) return "No LOTO date is on file to verify.";
  if (p.lotoVerifiedOn) return "That LOTO date is already verified.";
  if (verifierBadgeId === p.qewId) return "Someone else verifies your LOTO date.";
  const d = correctedDate.trim();
  if (d) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || Number.isNaN(Date.parse(`${d}T00:00:00Z`))) return "Pick a real date.";
    return lotoReportProblem(d, false, today);
  }
  return null;
}

/** Fields the verify writes on the person. A correction replaces the date and its expiry. */
export function verifyFields(
  p: Pick<Person, "lotoTrainedOn" | "lotoExpiresOn">,
  who: { badgeId: string; holderName: string },
  correctedDate: string,
  today: string,
  nowIso: string,
): Record<string, unknown> {
  const d = correctedDate.trim();
  const fields: Record<string, unknown> = {
    lotoVerifiedOn: today,
    lotoVerifiedAt: nowIso,
    lotoVerifiedBy: who.badgeId,
    lotoVerifiedByName: who.holderName,
    lotoVerifiedHow: "People & Badges",
  };
  if (d && d !== p.lotoTrainedOn) {
    fields.lotoTrainedOn = d;
    fields.lotoExpiresOn = addYears(d, LOTO_VALID_YEARS);
    fields.lotoCorrectedFrom = p.lotoTrainedOn ?? null;
  }
  return fields;
}

export function siteOfId(id: string): string {
  return id.slice(0, 2);
}

/** QEW IDs with all four nodes closed once: training complete. Expiry does not matter here (done once). */
export function allNodesDoneSet(records: { qewId: string; nodeId: string }[]): Set<string> {
  const seen = new Map<string, Set<string>>();
  for (const r of records) {
    const s = seen.get(r.qewId) ?? new Set<string>();
    s.add(r.nodeId);
    seen.set(r.qewId, s);
  }
  return new Set([...seen].filter(([, s]) => s.has("node1") && s.has("node2") && s.has("node3") && s.has("node4")).map(([id]) => id));
}

/** QEW IDs with Nodes 1, 2 and 3 each closed once. Opens gloves. Expiry does not matter here (done once). */
export function nodesOneToThreeDoneSet(records: { qewId: string; nodeId: string }[]): Set<string> {
  const seen = new Map<string, Set<string>>();
  for (const r of records) {
    const s = seen.get(r.qewId) ?? new Set<string>();
    s.add(r.nodeId);
    seen.set(r.qewId, s);
  }
  return new Set([...seen].filter(([, s]) => s.has("node1") && s.has("node2") && s.has("node3")).map(([id]) => id));
}
